import { useCallback, useRef, useState } from 'react'
import type { RefObject } from 'react'
import type { AgGridReact } from 'ag-grid-react'
import type { CellValueChangedEvent } from 'ag-grid-community'
import { useQueryClient } from '@tanstack/react-query'
import { patchItem, deleteItem } from '../../shared/api/requestItems.api'
import { saveProcurementTableNote } from '../../shared/api/procurementNotes.api'
import { rowStore } from './tableData'
import type { TableRow } from './types'

/**
 * Mapping from TableRow field → request_item API field name.
 * Fields not listed here are local-only or procurement-note fields.
 */
const REQUEST_ITEM_FIELD_MAP: Partial<Record<keyof TableRow, string>> = {
  partNumber: 'partNumber',
  brand: 'brand',
  qty: 'quantity',
  unit: 'unit',
  priority: 'priority',
  targetPrice: 'targetPrice',
  comments: 'noteForProcurement',
}

export interface ToastMessage {
  id: number
  text: string
  type: 'error' | 'success'
}

/** Аргументы низкоуровневого сохранения одной ячейки (без AG Grid event). */
export interface SaveFieldArgs {
  id: string
  field: keyof TableRow
  newValue: unknown
  oldValue: unknown
}

// Свежесозданные пустые строки (createEmptyRow) имеют id = Date.now().toString() —
// это НЕ DB-UUID, слать их в patchItem нельзя (404). Реальный id — UUID с дефисами.
function isPersistedId(id: string): boolean {
  return !!id && !/^\d{10,}$/.test(id)
}

export function useEditingEngine(
  gridRef: RefObject<AgGridReact<TableRow> | null>,
  procSlotUserMap?: Map<string, string>,
) {
  const [toasts, setToasts] = useState<ToastMessage[]>([])
  const queryClient = useQueryClient()

  // HIGH-8: ячейки, по которым идёт ПРОГРАММНЫЙ откат setDataValue. Откат
  // перевыпускает onCellValueChanged → saveCell → без guard'а снова шёл бы
  // сетевой запрос → бесконечный цикл PATCH. AG Grid доставляет cellValueChanged
  // АСИНХРОННО (setTimeout 0), поэтому полагаемся не на тайминг, а на СОВПАДЕНИЕ
  // значения: храним откатанное значение (`${id}:${field}` → oldValue) и в
  // saveCell гасим только то событие, чьё newValue равно откатанному. Так echo
  // ловится надёжно при любой (sync/async) доставке, а реальная повторная правка
  // (другое значение) проходит. setTimeout-fallback снимает ключ, если значение
  // не изменилось и события вообще не будет.
  const rollingBackRef = useRef<Map<string, unknown>>(new Map())

  // Дедупликация: если такой же текст уже висит — не плодим дубли.
  // При быстрых кликах по запрещённой ячейке пользователь видит одно окно,
  // а не 20 наложенных друг на друга.
  const showToast = useCallback((text: string, type: ToastMessage['type'] = 'error') => {
    setToasts((prev) => {
      if (prev.some((t) => t.text === text && t.type === type)) return prev
      const id = Date.now() + Math.random()
      setTimeout(() => setToasts((curr) => curr.filter((t) => t.id !== id)), 4000)
      return [...prev, { id, text, type }]
    })
  }, [])

  // Сетевая ошибка (offline, timeout, 5xx) достойна retry.
  // Валидационные 4xx — нет смысла повторять. Исключение — 429 (rate-limit):
  // при массовой вставке (HIGH-9) это «притормози», а не отказ → ретраим с
  // задержкой. Полноценное решение для больших вставок — bulk-PATCH (техдолг).
  const isNetworkError = (err: unknown): boolean => {
    const e = err as { code?: string; response?: { status?: number }; message?: string }
    if (e?.response?.status === 429) return true
    if (e?.response?.status && e.response.status < 500) return false
    if (e?.code === 'ERR_NETWORK' || e?.code === 'ECONNABORTED') return true
    if (e?.message && /network|timeout|fetch/i.test(e.message)) return true
    return !e?.response
  }

  // HIGH-9: числовые поля сервер принимает только числом (Zod z.number без
  // coerce) — буфер даёт строку, приводим. Возвращает [ok, value]: ok=false →
  // значение невалидно/пустой qty, персист пропускаем (quantity НЕ nullable).
  const coerceForApi = (apiField: string, raw: unknown): [boolean, unknown] => {
    if (apiField === 'quantity') {
      const n =
        typeof raw === 'number'
          ? raw
          : Number(
              String(raw ?? '')
                .trim()
                .replace(',', '.'),
            )
      if (!Number.isFinite(n) || n <= 0) return [false, null]
      return [true, n]
    }
    if (apiField === 'targetPrice') {
      const s = raw == null ? '' : String(raw).trim()
      if (s === '') return [true, null] // targetPrice nullable — пустой = null
      const n = Number(s.replace(',', '.'))
      if (!Number.isFinite(n)) return [false, null]
      return [true, n]
    }
    return [true, raw ?? null]
  }

  /**
   * Низкоуровневое сохранение одной ячейки на сервер. Переиспользуется и
   * редактором (saveCell), и буфером обмена (paste/Delete, HIGH-9). В отличие от
   * saveCell НЕ удаляет строку при пустом partNumber — пустой partNumber просто
   * не персистится (защита от массового удаления через Delete по колонке).
   */
  const saveField = useCallback(
    async ({ id, field, newValue, oldValue }: SaveFieldArgs) => {
      const requestItemApiField = REQUEST_ITEM_FIELD_MAP[field]
      const isProcSlot = procSlotUserMap?.has(field as string)
      if (!requestItemApiField && !isProcSlot) return // локальное поле — не сохраняем
      if (!isPersistedId(id)) return // новая пустая строка без DB-записи
      // Пустой партномер через буфер/Delete не персистим: в редакторе это команда
      // «удалить строку» (см. saveCell), а через буфер — почти всегда случайность.
      if (
        requestItemApiField === 'partNumber' &&
        (newValue == null || String(newValue).trim() === '')
      ) {
        return
      }

      // Числовые поля приводим к Number; невалидный/пустой qty не персистим.
      let valueToSave: unknown = newValue
      if (requestItemApiField) {
        const [ok, coerced] = coerceForApi(requestItemApiField, newValue)
        if (!ok) return
        valueToSave = coerced
      }

      const runSave = async () => {
        if (requestItemApiField) {
          await patchItem(id, { [requestItemApiField]: valueToSave ?? null })
          void queryClient.invalidateQueries({ queryKey: ['requests'] })
        } else if (isProcSlot) {
          await saveProcurementTableNote({ requestItemId: id, note: String(newValue ?? '') })
          void queryClient.invalidateQueries({ queryKey: ['procurement-table-notes'] })
        }
      }

      let lastErr: unknown
      try {
        await runSave()
      } catch (err: unknown) {
        lastErr = err
        if (isNetworkError(err)) {
          // Одна повторная попытка через 2 секунды — возможно сеть моргнула
          await new Promise((r) => setTimeout(r, 2000))
          try {
            await runSave()
            return
          } catch (retryErr) {
            lastErr = retryErr
          }
        }

        // Откат ячейки к старому значению — пользователь должен видеть реальное
        // состояние БД. HIGH-8: помечаем ключ ДО setDataValue, чтобы порождённый
        // им onCellValueChanged → saveCell не запустил повторное сохранение.
        const node = gridRef.current?.api?.getRowNode(String(id))
        if (node) {
          const cellKey = `${id}:${String(field)}`
          rollingBackRef.current.set(cellKey, oldValue ?? null)
          node.setDataValue(field as string, oldValue)
          // Анти-залипание: если значение не изменилось (echo-события не будет) —
          // снимаем ключ по таймауту. Задержка с запасом больше любого flush
          // AG Grid, чтобы не снять ключ до прихода настоящего echo.
          setTimeout(() => rollingBackRef.current.delete(cellKey), 2000)
        }

        const isNet = isNetworkError(lastErr)
        const apiMessage = (lastErr as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message
        const message = isNet
          ? 'Не удалось сохранить. Проверьте подключение'
          : (apiMessage ?? (lastErr instanceof Error ? lastErr.message : 'Ошибка сохранения'))
        showToast(message)
      }
    },
    [gridRef, showToast, procSlotUserMap, queryClient],
  )

  const saveCell = useCallback(
    async (e: CellValueChangedEvent<TableRow>) => {
      const field = e.colDef.field as keyof TableRow | undefined
      if (!field || !e.data.id) return

      // HIGH-8: если это echo нашего же отката (значение совпадает с откатанным) —
      // не сохраняем (иначе цикл PATCH). Если значение иное — пользователь успел
      // отредактировать ячейку заново, продолжаем сохранение.
      const cellKey = `${e.data.id}:${String(field)}`
      if (rollingBackRef.current.has(cellKey)) {
        const rolledBack = rollingBackRef.current.get(cellKey)
        rollingBackRef.current.delete(cellKey)
        if (Object.is(e.newValue ?? null, rolledBack ?? null)) return
      }

      const requestItemApiField = REQUEST_ITEM_FIELD_MAP[field]

      // Пустой partNumber В РЕДАКТОРЕ = удаление строки (намеренная команда).
      // Через буфер этот путь не идёт — saveField пустой partNumber игнорирует.
      if (
        requestItemApiField &&
        field === 'partNumber' &&
        (!e.newValue || String(e.newValue).trim() === '')
      ) {
        // Несохранённая строка (временный id) в БД не существует: DELETE по ней
        // отвечал 404 и падал необработанным промисом, а сама строка так и
        // оставалась в гриде. Удаляем её только локально.
        const persisted = isPersistedId(String(e.data.id))
        if (persisted) {
          await deleteItem(e.data.id)
        }
        const api = gridRef.current?.api
        const node = api?.getRowNode(String(e.data.id))
        if (node?.data) api?.applyTransaction({ remove: [node.data] })
        rowStore.current = rowStore.current.filter((r) => r.id !== e.data.id)
        if (persisted) void queryClient.invalidateQueries({ queryKey: ['requests'] })
        return
      }

      await saveField({ id: e.data.id, field, newValue: e.newValue, oldValue: e.oldValue })
    },
    [gridRef, saveField, queryClient],
  )

  return { saveCell, saveField, toasts }
}
